// =============================================================================
// DidYouKnowRail — "Did you know?" support rail beneath the hero lead story.
// Dramatization Engine, Phase 4 (docs/DRAMATIZATION-ENGINE.md).
//
// Renders the secondary dramatized angles the engine ships alongside the lead
// story (`leadStory.support`). Each item is a small card: an archetype chip,
// the hook, and the themed odds as green pills — the SAME visual language as
// HeroLeadCard's payoff (numbers count up, brand-tokened, mobile-first).
//
// Self-hides: returns null when there are no support items, so older cached
// payloads (no `support`) and matches with a single angle simply render
// nothing. All copy is engine-authored + already localized — never add
// recommendation language in static copy.
// =============================================================================

import { type CSSProperties, type ReactElement } from 'react';
import { useCountUp } from '../../hooks/useCountUp.js';
import { useTranslation } from '../../i18n/index.js';
import type { LeadStorySupport } from '../../hooks/useLeadStory.js';
import styles from './DidYouKnowRail.module.css';

function OddsPill({ label, price, delay }: { label: string; price: number; delay: number }): ReactElement {
  const display = useCountUp({ end: price, duration: 700, delay, decimals: 2, enabled: true });
  return (
    <span className={styles.pill}>
      <span>{label}</span>
      <strong className={styles.pillPrice}>{display}</strong>
    </span>
  );
}

export function DidYouKnowRail({ support }: { support: LeadStorySupport[] }): ReactElement | null {
  const { t } = useTranslation();

  if (!support || support.length === 0) return null;

  return (
    <section className={styles.section} aria-label={t('didYouKnow.title', { defaultValue: 'Did you know?' })}>
      <h3 className={styles.title}>{t('didYouKnow.title', { defaultValue: 'Did you know?' })}</h3>

      <ul className={styles.list}>
        {support.map((item, idx) => (
          <li
            key={`${item.archetype.id}-${idx}`}
            className={styles.card}
            style={{ '--dyk-delay': `${idx * 90}ms` } as CSSProperties & Record<'--dyk-delay', string>}
          >
            {item.archetype.label ? <span className={styles.chip}>{item.archetype.label}</span> : null}

            <p className={styles.hook}>{item.hook}</p>

            <div className={styles.oddsRow}>
              {item.odds.slice(0, 3).map((o, i) => (
                <OddsPill key={o.label} label={o.label} price={o.price} delay={300 + idx * 90 + i * 100} />
              ))}
              {item.marketLabel ? <span className={styles.marketLabel}>{item.marketLabel}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
